import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import themed from 'webapp/decorators/themed';
import didUpdate from 'webapp/modifiers/did-update';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormSelectComponent from 'webapp/components/form/select';

import type SourceType from 'webapp/models-custom/source-type';

export interface EnsureUniquenessSettings {
	ensureUnique: boolean;
	isAppend: boolean;
}

interface Args {
	model: EnsureUniquenessSettings;
	sourceType: SourceType;
	submitInProgress: boolean;
	onChange: (ensureUniqueObj: EnsureUniquenessSettings) => void;
	theme?: string;
}

const EnsureUniquenessStrategy = {
	APPEND: true,
	OVERRIDE: false,
};

@themed()
export default class MetricEditEnsureUniquenessComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;

	declare theme: string; // from @themed
	declare size: string; // from @themed

	// Initialize if models are not set
	@tracked ensureUnique = false;
	@tracked ensureUniqueStrategy = Number(EnsureUniquenessStrategy.OVERRIDE);
	private disableStrategyForSourceType = false;
	private defaultStrategyValueBySourceType = EnsureUniquenessStrategy.OVERRIDE;

	ensureUniqueStrategyOptions: LabelValuePairNumeric[] = [
		{ value: Number(EnsureUniquenessStrategy.APPEND), label: 'Append' },
		{ value: Number(EnsureUniquenessStrategy.OVERRIDE), label: 'Override' },
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.setDefaultStrategyFieldsBySourceType();
		this.updateLocalValues(true);
	}

	get isEnsureUniqueStrategyHidden(): boolean {
		return !this.ensureUnique || this.disableStrategyForSourceType;
	}

	get checkboxTooltipText(): string {
		let text = `When data with identical date/time is sent to ${this.accountService.shownCompanyName}, the system offers the option to either aggregate all the values based on the selected aggregation function or save only the last received value for that date/time. Enabling this option ensures that all the data is aggregated.`;

		if (!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
			text += `<br><a href="https://help.databox.com/master-overview-custom-api-integrations#data-aggregation" target="_blank">Read more</a>`;
		}

		return text;
	}

	triggerOnChange(): void {
		this.args.onChange({
			ensureUnique: this.ensureUnique,
			isAppend: !!this.ensureUniqueStrategy,
		});
	}

	setDefaultStrategyFieldsBySourceType() {
		const sourceType = this.args.sourceType;

		if (sourceType.isToken || sourceType.isZapier || sourceType.isMake) {
			this.defaultStrategyValueBySourceType = EnsureUniquenessStrategy.APPEND;
		} else if (sourceType.isSheets || sourceType.isSqlishConnector) {
			this.defaultStrategyValueBySourceType = EnsureUniquenessStrategy.OVERRIDE;
			this.disableStrategyForSourceType = true;
		} else {
			this.defaultStrategyValueBySourceType = EnsureUniquenessStrategy.OVERRIDE;
		}
	}

	@action
	updateLocalValues(isInitialCheck = false): void {
		const ignorePreviousValues = isInitialCheck === true;
		if (ignorePreviousValues || this.ensureUnique !== this.args.model.ensureUnique) {
			this.ensureUnique = this.args.model.ensureUnique;
		}

		const _ensureUniqueStrategy = Number(this.args.model.isAppend);
		if (ignorePreviousValues || this.ensureUniqueStrategy !== _ensureUniqueStrategy) {
			this.ensureUniqueStrategy = _ensureUniqueStrategy;
		}
	}

	@action
	changeEnsureUnique(value: boolean): void {
		this.ensureUnique = value;
		this.changeEnsureUniqueStrategy(
			Number(
				value ? this.defaultStrategyValueBySourceType : EnsureUniquenessStrategy.OVERRIDE,
			),
		);
	}

	@action
	changeEnsureUniqueStrategy(value: number): void {
		this.ensureUniqueStrategy = value;
		this.triggerOnChange();
	}
	<template>
		<div
			class="ensure-uniqueness parts-flex
				{{unless this.isEnsureUniqueStrategyHidden 'parts-flex--2-items'}}"
			{{didUpdate
				(fn this.updateLocalValues false)
				@model.ensureUnique
				this.ensureUniqueStrategy
			}}
		>
			<div class="part">
				<FormCheckboxComponent
					@name="ensure_unique"
					@selected={{this.ensureUnique}}
					@onChange={{this.changeEnsureUnique}}
					@label="Handling multiple values stored for identical date/time"
					@isDisabled={{@submitInProgress}}
					@theme={{this.theme}}
					@item={{hash tooltip=this.checkboxTooltipText}}
				/>
			</div>
			{{#unless this.isEnsureUniqueStrategyHidden}}
				<div class="part">
					<FormSelectComponent
						name="ensure_unique_strategy"
						@selected={{this.ensureUniqueStrategy}}
						@options={{this.ensureUniqueStrategyOptions}}
						@isNumeric={{true}}
						@onChange={{this.changeEnsureUniqueStrategy}}
						@isRequired={{true}}
						@isDisabled={{@submitInProgress}}
						@theme={{this.theme}}
					/>
				</div>
			{{/unless}}
		</div>
	</template>
}
